/**
 * An operator's public announcements for the people who park (logbook
 * decision 20), read from parking-backend's public endpoint: no sign-in, the
 * same for everyone, cached by the server for a minute.
 *
 *   GET /operator/api/public/messages?operator=<alias>[&zone=<uuid>]
 *   -> { operator, messages: [{ id, zoneIds, subject, body, push, startsAt, endsAt, updatedAt }] }
 *
 * Only live messages come back. With `zone`, only those for the whole
 * operator or naming that zone.
 *
 * The shell shows them; the mounted modules do not (decision 21).
 */

export interface PublicMessage {
  id: string
  zoneIds: string[] | null
  subject: string
  body: string
  push: boolean
  startsAt: string | null
  endsAt: string | null
  updatedAt: string | null
}

const MESSAGES_URL =
  import.meta.env.VITE_MESSAGES_URL || 'https://api.parkeze.com/operator/api/public/messages'

/** An org alias as Keycloak and the operators table spell it. */
export function isOrgAlias(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9-]{1,64}$/.test(value)
}

export async function fetchPublicMessages(
  operator: string,
  zone?: string | null,
  signal?: AbortSignal,
): Promise<PublicMessage[]> {
  const params = new URLSearchParams({ operator })
  if (zone) params.set('zone', zone)
  const resp = await fetch(`${MESSAGES_URL}?${params}`, { signal, headers: { Accept: 'application/json' } })
  // An alias the backend does not know is "no announcements", not a failure.
  if (resp.status === 404) return []
  if (!resp.ok) throw new Error(`Announcements request failed: ${resp.status}`)
  const body = (await resp.json()) as { messages?: unknown }
  return Array.isArray(body.messages) ? body.messages.filter(isMessage) : []
}

function isMessage(value: unknown): value is PublicMessage {
  if (!value || typeof value !== 'object') return false
  const m = value as Record<string, unknown>
  return typeof m.id === 'string' && typeof m.subject === 'string' && typeof m.body === 'string'
}

/** Started and not yet ended, by this browser's clock. */
export function isLive(message: PublicMessage, now = Date.now()): boolean {
  const starts = message.startsAt ? Date.parse(message.startsAt) : NaN
  const ends = message.endsAt ? Date.parse(message.endsAt) : NaN
  if (!Number.isNaN(starts) && starts > now) return false
  if (!Number.isNaN(ends) && ends <= now) return false
  return true
}

// --- Dismissals ---
//
// Remembered per message id in this browser. Storage can be missing or
// refuse (private windows, blocked site data): then a dismissal lasts until
// the page reloads, and nothing breaks.

const DISMISSED_KEY = 'parkeze.announcements.dismissed'
const DISMISSED_LIMIT = 200

export function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

export function writeDismissed(ids: string[]): void {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-DISMISSED_LIMIT)))
  } catch {
    // Not remembered past this page; still hidden for now.
  }
}
